<template>
    <Portal to="modal">
        <ModalContainer key="UtiDiscountPageModal">
            <template #modal>
                <Modal class="uti-discount-page__modal">
                    <div v-if="!isShowingPaymentSuccess" class="uti-discount-page__head">
                        <UtiHeader
                            :current-step="currentStep"
                            @back="goBack"
                        />
                    </div>
                    <div
                        class="uti-discount-page"
                        :class="{
                            'uti-discount-page--success': isShowingPaymentSuccess,
                        }"
                    >
                        <h1
                            v-if="!isShowingPaymentSuccess"
                            class="uti-discount-page__title"
                            tabindex="-1"
                            role="heading"
                        >
                            <template v-if="currentStep === 1">
                                Account Details
                            </template>
                            <template v-else-if="currentStep === 2">
                                Add Payment & Review
                            </template>
                        </h1>
                        <div
                            v-if="!isShowingPaymentSuccess && currentStep === 2"
                            class="uti-discount-page__subtitle"
                            :class="{
                                'uti-discount-page__subtitle--error': paymentError,
                            }"
                        >
                            <template v-if="paymentError">
                                {{ paymentError }}
                            </template>
                        </div>
                        <UtiAccountDetails
                            v-if="currentStep === 1"
                            :exam-guids="examGuids || []"
                            :org-id-name-lib="orgIdNameLib"
                            @submit="submitAccountDetails"
                        />
                        <PaymentFields
                            v-if="currentStep === 2 && stripePrice && !isShowingPaymentSuccess"
                            :key="paymentFieldsRefreshTime"
                            mode="payment"
                            :price-id="stripePrice.id"
                            @checkoutCompleted="checkoutCompleted"
                            @error="handlePaymentFieldsError"
                        />
                        <MountainSuccess
                            v-else-if="isShowingPaymentSuccess"
                            class="uti-discount-page__mountain-success"
                            :is-loading="isLoadingPremiumActivation"
                            :errorMessage="premiumActivationError"
                            @close="goToStudy"
                        />
                        <Errors
                            v-if="!isLoading && errorMessage"
                            class="uti-discount-page__error-message"
                            :errors="[ errorMessage ]"
                        />
                    </div>
                </Modal>
            </template>
        </ModalContainer>
    </Portal>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as Sentry from '@sentry/browser'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import UtiHeader from '@/components/UTI/UtiHeader.vue'
import UtiAccountDetails from '@/components/UTI/UtiAccountDetails.vue'
import PaymentFields from '@/components/PaymentFields.vue'
import MountainSuccess from '@/components/MountainSuccess.vue'
import { subscriptionModule } from '@/store/subscription/module'
import { stripeModule } from '@/store/stripe/module'

const {
    ModalContainer,
    Modal,
    Errors,
} = UIKit

const route = useRoute()
const router = useRouter()

const currentStep = ref(1)
const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const createPassword = ref(false)
const isLoading = ref(false)
const isLoadingPremiumActivation = ref(false)
const errorMessage = ref('')
const agreed = ref(false)
const agreeDisabled = ref(false)
const examGuids = ref<string[] | null>(null)
const orgIdNameLib = ref<{ [orgId: string]: string } | null>(null)
const stripePrice = ref<{
    id: string
    object: 'price'
    created: number
    nickname: string | null
    unit_amount: number | null
    unit_amount_decimal: string | null
} | null>(null)
const paymentFieldsRefreshTime = ref(Date.now())
const paymentError = ref('')
const premiumActivationError = ref('')
const isShowingPaymentSuccess = ref(false)

const orgId = computed(() => {
    return typeof route.query.orgId === 'string' ? route.query.orgId : null
})

onMounted(async () => {
    const routeStep = route.query.step
    if (routeStep === 'account') {
        currentStep.value = 1
    } else if (routeStep === 'payment') {
        currentStep.value = 2
    }

    // Fetch UTI-specific discount data
    const utiConfig = await subscriptionModule.actions.fetchUTIConfig()
    examGuids.value = utiConfig.examGuids
    orgIdNameLib.value = utiConfig.orgIdNameLib
    stripePrice.value = utiConfig.stripePrice

    const registeredUser = userModule.state.user
    firstName.value = registeredUser?.firstName || ''
    lastName.value = registeredUser?.lastName || ''
    email.value = registeredUser?.email || ''
    agreed.value = !!registeredUser
    agreeDisabled.value = !!registeredUser
    createPassword.value = !!password.value
})

const updateRouteStep = async (stepName: string) => {
    if (route.query.step !== stepName) {
        await router.replace({
            name: 'uti-discount',
            query: {
                ...route.query,
                step: stepName,
            },
        })
    }

    focusTitle()
}


const focusTitle = () => {
    setTimeout(() => {
        const titleEl = document.querySelector('.uti-discount-page__title') as HTMLElement
        titleEl?.focus()
    }, 1)
}

const scrollTop = () => {
    const registerEl = document.querySelector('.uti-discount-page')
    registerEl?.scrollTo(0, 0)
}

const submitAccountDetails = () => {
    if (!orgId.value) {
        errorMessage.value = 'Must select a campus.'
        return
    }
    currentStep.value = 2
    updateRouteStep('payment')

    scrollTop()
}

const checkoutCompleted = async (checkoutSessionId: string) => {
    try {
        const checkoutSession = await stripeModule.actions.fetchCheckoutSession({
            checkoutSessionId: checkoutSessionId,
        })

        if (checkoutSession.status !== 'complete') {
            Sentry.captureException(new Error(`Incomplete checkout status for checkout session ${checkoutSessionId}`))
            paymentFieldsRefreshTime.value = Date.now()
            paymentError.value = 'Your payment failed or was canceled. Please try again.'
            nextTick(() => {
                scrollTop()
            })
            return
        }

        isLoadingPremiumActivation.value = true
        isShowingPaymentSuccess.value = true

        await subscriptionModule.actions.activateDiscountPurchase({
            organizationId: orgId.value || '',  // orgId was validated prior to payment
            checkoutSessionId,
        })

        await userModule.actions.updateWebConfig({
            hasSeenWelcomeModal: true,
        })
    } catch (err) {
        Sentry.captureException(err)
        premiumActivationError.value =
            'There was an error updating your account. Try refreshing your page or contacting support.'
        await userModule.actions.fetchUserData({ stripeSubs: true })
    }
    isLoadingPremiumActivation.value = false
}

const handlePaymentFieldsError = async () => {
    paymentError.value = 'Error loading payment fields. Try refreshing your page or contacting support.'
    await userModule.actions.fetchUserData({ stripeSubs: true })
}

const goToStudy = async () => {
    await router.push({ name: 'study' })
}

const goBack = () => {
    currentStep.value = 1
    updateRouteStep('account')
    focusTitle()
}
</script>

<style lang="scss" scoped>
.uti-discount-page {
    width: 1010px;
    background: $gray-background;
    min-height: 640px;
    height: calc(100% - 56px);
    box-sizing: border-box;
    overflow: auto;
    border-radius: 0 0 12px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;

    @include breakpoint(brown-bear) {
        width: 740px;
        height: 100% !important;
    }

    @include breakpoint(black-bear) {
        max-height: none;
        height: calc(100vh - 56px) !important;
        width: 100%;
        min-height: 0;
    }

    &--success {
        overflow: hidden;
        height: 100% !important;
        padding: 0;
        background-color: $floral-white;
    }

    &__modal {
        position: absolute;
        top: 72px;
        left: 50%;
        transform: translate(-50%);
        overflow: hidden;
        height: calc(86vh);

        @include breakpoint(brown-bear) {
            top: 69px;
        }

        @include breakpoint(black-bear) {
            height: auto;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            transform: none;
            margin: 0;
            border-radius: 0;
            box-shadow: none;
            overflow: auto;
        }
    }

    &__head {
        position: relative;
        width: 100%;
        height: 56px;
        display: flex;
        padding: 0 18px;
        box-sizing: border-box;
        align-items: center;
        border-bottom: 1px solid rgba($pewter, 0.3);

        @include breakpoint(brown-bear) {
            padding: 0 16px;
        }
    }

    &__existing-account {
        display: none;

        @include breakpoint(brown-bear) {
            display: block;
            margin: 12px 0 5px;
            text-align: center;
        }
    }

    &__step {
        font-size: 13px;
        line-height: 18px;
        text-align: center;
        color: $slate-03;
        padding-top: 23px;
        margin-bottom: 10px;

        &--referral {
            padding-top: 40px;
        }

        @include breakpoint (black-bear) {
            padding-top: 21px;
        }
    }

    &__title {
        font-size: 26px;
        outline: none;
        line-height: 36px;
        font-weight: 700;
        text-align: center;
        margin: 0;
        margin-top: 36px;
        padding: 0 25px;

        @include breakpoint(black-bear) {
            max-width: 323px;
            margin-top: 24px;
        }
    }

    &__subtitle {
        color: #475967;
        font-size: 14px;
        line-height: 19px;
        text-align: center;
        margin-bottom: 36px;
        margin-top: 4px;
        width: 350px;

        @include breakpoint(brown-bear) {
            margin-bottom: 4px;
        }

        &--error {
            color: $pepper;
        }
    }

    &__mountain-success {
        position: relative;
        width: 100%;
        height: 100%;
        background-color: $floral-white;
        padding-top: 78px;
        padding-bottom: 32px;

        @include breakpoint(black-bear) {
            padding-top: 42px;
        }
    }
}
</style>
